import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Copy, Check, ExternalLink } from 'lucide-react';

export interface Event {
    id: number;
    txHash: string;
    eventName: string;
    eventData: {
        [key: string]: any;
    };
    blockNumber: number;
    logIndex: number;
    eventTimestamp: number; // Unix timestamp in seconds
    contractAddress: string;
    createdAt: string;
    updatedAt: string;
}

interface EventTableRowProps {
    event: Event;
    isExpanded: boolean;
    onToggleExpand: (eventId: string) => void;
    formatTimestamp: (timestamp: number) => string;
    truncateHash: (hash: string) => string;
}

export const EventTableRow: React.FC<EventTableRowProps> = ({ event, isExpanded, onToggleExpand, formatTimestamp, truncateHash }) => {
    const [copied, setCopied] = useState(false);
    const navigate = useNavigate();

    const handleRowClick = () => {
        onToggleExpand(String(event.id));
    };

    const handleViewDetails = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent row expansion when clicking button
        navigate(`/events/${event.txHash}`);
    };

    const handleCopyHash = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent row expansion when clicking copy
        navigator.clipboard.writeText(event.txHash);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const capitalizeKey = (key: string): string => {
        // Handle special cases first
        let result = key;

        // Replace 'Id' with 'ID' at the end of words
        result = result.replace(/Id$/g, 'ID');
        result = result.replace(/Id([A-Z])/g, 'ID$1');

        // Keep EKYC as one word
        result = result.replace(/^ekyc/i, 'EKYC');
        result = result.replace(/([a-z])Ekyc/g, '$1EKYC');

        // Split by capital letters and join with spaces
        const words = result.replace(/([A-Z])/g, ' $1').trim();

        // Capitalize first letter of each word
        let capitalized = words.charAt(0).toUpperCase() + words.slice(1);

        // Remove ALL spaces between capital letters (for acronyms)
        // This will handle cases like "U I D", "N I C", etc.
        while (capitalized.match(/([A-Z])\s+([A-Z])/)) {
            capitalized = capitalized.replace(/([A-Z])\s+([A-Z])/g, '$1$2');
        }

        return capitalized;
    };

    const formatTimestampISO = (timestamp: number): string => {
        const date = new Date(timestamp * 1000);
        return date.toLocaleString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata',
        });
    };

    const renderValue = (value: any, depth: number = 0): React.ReactNode => {
        if (value === null || value === undefined) {
            return <span className="inline-block px-2 py-1 bg-gray-100 text-gray-400 rounded text-xs">null</span>;
        }

        if (typeof value === 'boolean') {
            return <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${value ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-red-100 text-red-700 border border-red-300'}`}>{String(value)}</span>;
        }

        if (typeof value === 'number') {
            // Check if it might be a timestamp (Unix timestamp in seconds - reasonable range)
            if (value > 1000000000 && value < 3000000000) {
                return <span className="inline-block px-2 py-1 bg-green-50 text-green-700 rounded text-xs border border-green-200 font-medium">{formatTimestampISO(value)}</span>;
            }
            return <span className="inline-block px-2 py-1 bg-brand-light text-brand rounded text-xs border border-blue-200 font-medium">{value}</span>;
        }

        if (typeof value === 'string') {
            // Try to parse as JSON if it looks like JSON
            if ((value.startsWith('[') && value.endsWith(']')) || (value.startsWith('{') && value.endsWith('}'))) {
                try {
                    const parsed = JSON.parse(value);
                    return renderValue(parsed, depth);
                } catch {
                    // Not valid JSON, continue with string rendering
                }
            }

            // Check if it's a hash
            if (/^0x[a-fA-F0-9]+$/.test(value)) {
                return <span className="inline-block px-2 py-1 bg-purple-50 text-purple-700 font-mono text-xs rounded border border-purple-200">{value}</span>;
            }
            return <span className="inline-block px-2 py-1 bg-gray-50 text-gray-800 rounded text-xs border border-gray-200">{value}</span>;
        }

        if (Array.isArray(value)) {
            if (value.length === 0) {
                return <span className="inline-block px-2 py-1 bg-gray-100 text-gray-400 rounded text-xs">empty array</span>;
            }
            return (
                <div className="inline-block ml-4 space-y-2">
                    {value.map((item, index) => (
                        <div key={index} className="flex items-start gap-2 bg-slate-50 rounded p-2 border border-slate-200 w-fit">
                            <span className="inline-block px-1.5 py-1 bg-slate-200 text-slate-700 rounded text-xs font-medium min-w-[24px] text-center">{index}</span>
                            <div>{renderValue(item, depth + 1)}</div>
                        </div>
                    ))}
                </div>
            );
        }

        if (typeof value === 'object') {
            const entries = Object.entries(value);
            if (entries.length === 0) {
                return <span className="inline-block px-2 py-1 bg-gray-100 text-gray-400 rounded text-xs">empty object</span>;
            }
            return (
                <div className="inline-block ml-4 space-y-2">
                    {entries.map(([key, val]) => (
                        <div key={key} className="flex items-start gap-2 bg-brand-light rounded p-2 border border-brand w-fit">
                            <span className="inline-block px-2 py-1 bg-brand-light text-brand rounded text-xs font-semibold whitespace-nowrap">{capitalizeKey(key)}</span>
                            <div>{renderValue(val, depth + 1)}</div>
                        </div>
                    ))}
                </div>
            );
        }

        return <span className="inline-block px-2 py-1 bg-gray-50 text-gray-800 rounded text-xs border border-gray-200">{String(value)}</span>;
    };

    return (
        <React.Fragment>
            <tr onClick={handleRowClick} className="hover:bg-gray-50 transition-colors cursor-pointer">
                <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-brand-light text-brand">{event.eventName}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-900 font-mono">{truncateHash(event.txHash)}</span>
                        <button onClick={handleCopyHash} className="p-1 hover:bg-gray-200 rounded transition-colors" title="Copy transaction hash">
                            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-600" />}
                        </button>
                    </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{event.blockNumber.toLocaleString()}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{formatTimestamp(event.eventTimestamp)}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-center gap-2">
                        <button onClick={handleViewDetails} className="p-1.5 hover:bg-blue-100 rounded transition-colors text-blue-600 hover:text-blue-700" title="View details">
                            <ExternalLink className="w-4 h-4" />
                        </button>
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
                    </div>
                </td>
            </tr>
            {isExpanded && (
                <tr className="bg-gray-50">
                    <td colSpan={5} className="px-6 py-4">
                        <div className="space-y-3">
                            <h4 className="font-semibold text-gray-900 mb-3 text-base">Event Data</h4>
                            {event.eventData && Object.keys(event.eventData).length > 0 ? (
                                <div className="bg-white rounded-lg border border-gray-300 p-4 shadow-sm">
                                    <div className="text-sm leading-relaxed space-y-3">
                                        {Object.entries(event.eventData).map(([key, value]) => (
                                            <div key={key} className="flex items-start gap-2 bg-brand-light rounded-lg p-3 border border-brand w-fit">
                                                <span className="inline-block px-2.5 py-1 bg-brand text-white rounded text-xs font-semibold whitespace-nowrap border border-brand">{capitalizeKey(key)}</span>
                                                <div className="">{renderValue(value, 0)}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-white rounded-lg border border-gray-200 p-4 text-center text-gray-500 italic">No event data available</div>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </React.Fragment>
    );
};
